"use client";

import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "next/navigation";
import type {
  CheckoutAddonDTO,
  PublicActivityDTO,
  PublicAvailabilityUnitDTO,
  PublicBookingSiteDTO,
  PublicFoodItemDTO,
  PublicStayPackageDTO,
} from "@/lib/types";
import { useI18n } from "@/i18n/I18nProvider";
import { localizeError } from "@/i18n/errors";
import { PublicBookingSiteView } from "@/components/PublicBookingSiteView";
import { useSiteTracking } from "@/components/TrackingProvider";

export default function PublicBookingSitePage() {
  const params = useParams();
  const slug = params.slug as string;
  const { t, locale } = useI18n();

  const [site, setSite] = useState<PublicBookingSiteDTO | null>(null);
  const [units, setUnits] = useState<PublicAvailabilityUnitDTO[]>([]);
  const [activities, setActivities] = useState<PublicActivityDTO[]>([]);
  const [food, setFood] = useState<PublicFoodItemDTO[]>([]);
  const [packages, setPackages] = useState<PublicStayPackageDTO[]>([]);
  const [addons, setAddons] = useState<CheckoutAddonDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSite = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Resolve by slug or host
      const res = await fetch(`/api/public/sites/resolve?hostname=${encodeURIComponent(slug)}`);
      const data = await res.json();
      if (!data.success || !data.data) {
        setError(data.error ? localizeError(locale, data.error) : t("publicsite.page.notFound"));
        return;
      }

      const siteDto: PublicBookingSiteDTO = data.data;
      setSite(siteDto);

      // Fetch units and catalog
      const [unitsRes, catRes, pkgRes] = await Promise.all([
        fetch(`/api/public/sites/units?hostname=${encodeURIComponent(siteDto.hostname)}`),
        fetch(`/api/public/sites/catalog?hostname=${encodeURIComponent(siteDto.hostname)}`),
        fetch(`/api/public/sites/packages?hostname=${encodeURIComponent(siteDto.hostname)}`),
      ]);

      const [unitsData, catData, pkgData] = await Promise.all([unitsRes.json(), catRes.json(), pkgRes.json()]);
      if (pkgData.success) setPackages(pkgData.data || []);
      if (unitsData.success) setUnits(unitsData.data || []);
      if (catData.success && catData.data) {
        setActivities(catData.data.activities || []);
        setFood(catData.data.food || []);
        setAddons(catData.data.addons || []);
      }
    } catch {
      setError(t("publicsite.page.loadError"));
    } finally {
      setLoading(false);
    }
  }, [slug, t, locale]);

  useEffect(() => {
    loadSite();
  }, [loadSite]);

  // The owner's own GA4 / Meta Pixel, when their plan includes it.
  useSiteTracking(site?.tracking);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-xs text-slate-400">
        {t("publicsite.page.loading")}
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center text-xs text-slate-400">
        <div className="p-6 bg-slate-900 border border-slate-800 rounded-2xl max-w-md space-y-3">
          <div className="text-2xl">{"🏨"}</div>
          <h2 className="text-base font-bold text-white">{t("publicsite.page.notFoundTitle")}</h2>
          <p className="text-slate-400 text-xs">
            {error || t("publicsite.page.notFoundDesc")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <PublicBookingSiteView
      site={site}
      units={units}
      activities={activities}
      food={food}
      packages={packages}
      addons={addons}
      isPreview={false}
    />
  );
}
